import * as THREE from 'three';
import { makeProduct } from './products';
import { PREMIUM_BLACK_ALBEDO } from './finishAppearance';

/** Small, isolated product studio using the SAME geometry as the showroom. */
export function createProductPreviewScene(library) {
  const renderer = new THREE.WebGLRenderer({ antialias: true });
  renderer.setPixelRatio(1); renderer.setSize(384, 256);
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  const scene = new THREE.Scene(); scene.background = new THREE.Color('#f7f2e8');
  scene.add(new THREE.HemisphereLight('#ffffff', '#8a9285', 1.8));
  const sun = new THREE.DirectionalLight('#fff9f0', 2.2); sun.position.set(3, 5, 5); scene.add(sun);
  const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, .01, 100);
  const base = new THREE.MeshStandardMaterial({ color: PREMIUM_BLACK_ALBEDO, metalness: .08, roughness: .76 });
  const glass = new THREE.MeshBasicMaterial({ color: '#9ebbb4', transparent: true, opacity: .28, side: THREE.DoubleSide, depthWrite: false });
  const steel = new THREE.MeshStandardMaterial({ color: '#bac5bd', metalness: .35, roughness: .4 });
  const thermal = new THREE.MeshStandardMaterial({ color: '#445446', roughness: 1 });
  const gasket = new THREE.MeshStandardMaterial({ color: '#26322a', roughness: 1 });
  const wall = new THREE.MeshStandardMaterial({ color: '#d8d8cc', roughness: 1 });
  const previewLibrary = { ...library, makeFrame: () => base.clone(), glass, glassEdge: glass, steel, thermal, gasket, wall, cutAluminium: steel };

  return {
    async render(product) {
      let model;
      try {
        model = makeProduct(product, previewLibrary); scene.add(model.group);
        const bounds = new THREE.Box3().setFromObject(model.group);
        const center = bounds.getCenter(new THREE.Vector3()), size = bounds.getSize(new THREE.Vector3());
        camera.position.copy(center).add(new THREE.Vector3(.3, .13, 1).normalize().multiplyScalar(size.length() * 3));
        camera.lookAt(center); camera.updateMatrixWorld(true);
        const cameraBounds = new THREE.Box3();
        for (const x of [bounds.min.x, bounds.max.x]) for (const y of [bounds.min.y, bounds.max.y]) for (const z of [bounds.min.z, bounds.max.z]) {
          cameraBounds.expandByPoint(new THREE.Vector3(x, y, z).applyMatrix4(camera.matrixWorldInverse));
        }
        const projected = cameraBounds.getSize(new THREE.Vector3());
        const halfHeight = Math.max(projected.y, projected.x / 1.5) * .56;
        camera.left = -halfHeight * 1.5; camera.right = halfHeight * 1.5;
        camera.top = halfHeight; camera.bottom = -halfHeight; camera.updateProjectionMatrix();
        renderer.render(scene, camera);
        const blob = await new Promise(resolve => renderer.domElement.toBlob(resolve, 'image/webp', .86));
        if (!blob) throw new Error('Product preview encoding failed');
        return await new Promise((resolve, reject) => {
          const reader = new FileReader(); reader.onload = () => resolve(reader.result);
          reader.onerror = () => reject(reader.error); reader.readAsDataURL(blob);
        });
      } finally {
        if (model) {
          scene.remove(model.group);
          model.group.traverse(o => { o.geometry?.dispose(); if (o.material?.userData?.role === 'insect-mesh') { o.material.map?.dispose(); o.material.dispose(); } });
          model.frame.dispose();
        }
      }
    },
    dispose() {
      renderer.dispose(); renderer.forceContextLoss();
      [base, glass, steel, thermal, gasket, wall].forEach(material => material.dispose());
    },
  };
}